import { useCallback } from "react";

import { ConfigFileStatus, ConfigFileStatusType } from "../../../entities/ConfigFileStatus";
import { useTerminalContext } from "../contexts/TerminalContext";
import { configApi } from "../services/configApi";
import { createWithLoading, wrapError } from "../utils/configApiHelpers";
import { useConfigFileLoader } from "./useConfigFileLoader";

/**
 * Hook for uploading config files
 */
export const useConfigFileUploader = () => {
  const { setIsLoading, setFileList } = useTerminalContext();
  const { loadConfigFile } = useConfigFileLoader();

  const uploadConfigFile = useCallback(
    async (
      terminalId: number,
      file: File,
      status: ConfigFileStatusType = ConfigFileStatus.draft
    ) => {
      const withLoading = createWithLoading(setIsLoading);
      return withLoading(async () => {
        try {
          const uploadResponse = await configApi.postConfigFile(terminalId, file, status);
          const refreshedFiles = await configApi.fetchConfigFiles(terminalId);
          setFileList(refreshedFiles);
          const sortedRefreshed = [...refreshedFiles].sort(
            (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
          );

          // Try to open the freshly uploaded file (preferred), otherwise the newest available
          const uploadedSrc =
            uploadResponse?.body?.file?.filename ||
            (uploadResponse as any)?.file?.filename; // Fallback if backend returns a flat shape
          const uploadedStatus =
            refreshedFiles.find((f) => f.src === uploadedSrc)?.status ?? status;

          if (uploadedSrc) {
            await loadConfigFile(terminalId, uploadedSrc, uploadedStatus);
          } else if (sortedRefreshed[0]) {
            const newestFile = sortedRefreshed[0];
            await loadConfigFile(terminalId, newestFile.src, newestFile.status);
          }
        } catch (error: any) {
          console.error("Upload failed:", error);
          throw wrapError("Upload failed", error);
        }
      });
    },
    [loadConfigFile, setFileList, setIsLoading]
  );

  return { uploadConfigFile };
};
